/**
 * 补录老顾客。
 *
 * 目的很窄：让复购提醒有据可依。以前做过全口/局部的人，几年后该换新，
 * 顾客自己绝不会记得 —— 但系统里得先知道「他做过、哪一年做的」。
 *
 * 所以【只有四个格子】，而且填完不关窗，直接清空等下一个：
 * 老板是照着账本一条条抄的，每录一个都要重新点开抽屉的话，
 * 三个人之后他就不想录了。
 *
 * 不问金额、不问牙位、不问流程。凭记忆填金额就是在编造账目，
 * 而账目一旦掺假，整个系统就不能信了。
 */

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, History } from 'lucide-react'
import { api } from '../lib/api'
import { Button, Drawer, Field, Input, Segmented, useToast } from '../components/ui'
import { feedback } from '../lib/feedback'

interface Result {
  customerName: string
  customerCode: string
  createdCustomer: boolean
  caseNo: string
  typeLabel: string
  dueForRenewal: boolean
}

export function BackfillDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const nameRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [type, setType] = useState<string | null>(null)
  const [year, setYear] = useState('')
  const [lang, setLang] = useState<'ms' | 'en' | 'zh'>('ms')
  const [done, setDone] = useState<Result[]>([])
  const [error, setError] = useState<string | null>(null)

  const meta = useQuery({
    queryKey: ['case-meta'],
    queryFn: () =>
      api.get<{ types: Array<{ code: string; label: string; renewYears?: number }> }>(
        '/api/cases/meta',
      ),
    enabled: open,
  })

  // 只列会提醒换新的类型 —— 补录维修单对复购提醒毫无帮助，
  // 把它们摆出来只会让老板浪费时间录没用的数据
  const types = (meta.data?.types ?? []).filter((t) => (t.renewYears ?? 0) > 0)

  useEffect(() => {
    if (!open) return
    setName('')
    setPhone('')
    setYear('')
    setError(null)
    setDone([])
    setType(types[0]?.code ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (open && type === null && types.length > 0) setType(types[0]!.code)
  }, [open, type, types])

  const thisYear = new Date().getFullYear()

  const save = useMutation({
    mutationFn: () =>
      api.post<{ item: Result }>('/api/cases/backfill', {
        fullName: name.trim(),
        phone: phone.trim(),
        type,
        year: Number(year),
        language: lang,
      }),
    onSuccess: (r) => {
      feedback('done')
      setDone((d) => [r.item, ...d])
      void qc.invalidateQueries({ queryKey: ['customers'] })
      void qc.invalidateQueries({ queryKey: ['reminder-counts'] })
      // 清空，等下一个 —— 不关窗
      setName('')
      setPhone('')
      setYear('')
      setError(null)
      nameRef.current?.focus()
    },
    onError: (e) => setError((e as Error).message),
  })

  const sweep = useMutation({
    mutationFn: () => api.post<{ checkup: number; renewal: number }>('/api/reminders/sweep'),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['reminder-counts'] })
      void qc.invalidateQueries({ queryKey: ['reminders'] })
      const n = r.checkup + r.renewal
      toast.show(n > 0 ? `${n} 位顾客已放进「待通知」` : '暂时没有需要跟进的')
    },
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  const yearNum = Number(year)
  const yearOk = year.length === 4 && yearNum >= 1970 && yearNum <= thisYear
  const canSave = name.trim().length > 0 && phone.trim().length >= 3 && Boolean(type) && yearOk

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="lg"
      title="补录老顾客"
      footer={
        <>
          <Button size="lg" onClick={onClose}>
            {done.length > 0 ? `完成（已录 ${done.length} 位）` : '关闭'}
          </Button>
          <Button
            size="lg"
            variant="primary"
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            录入并继续
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <p className="rounded-md border border-line bg-sunken px-4 py-3 text-[15px] text-ink-2">
          只需要四样：<strong>姓名、电话、做的什么、哪一年</strong>。
          不用填金额 —— 补录是为了以后能提醒顾客换新，不是补记账目。
          录进来的单不会算进营业额。
        </p>

        {error && (
          <div className="rounded-md border border-bad-line bg-bad-bg px-4 py-3 text-[16px] text-bad">
            {error}
          </div>
        )}

        <Field label="姓名" required>
          <Input
            ref={nameRef}
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="照账本抄"
          />
        </Field>

        <Field label="电话" required hint="以后要靠这个号码通知他">
          <Input
            inputMode="tel"
            className="tnum"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="012-3456789"
          />
        </Field>

        <Field label="做的什么" required>
          {types.length === 0 ? (
            <p className="text-[16px] text-ink-3">
              还没有任何服务类型设了「多久之后该换新」。
              先去<strong>设置 → 服务与价格</strong>里给全口/局部填上年数。
            </p>
          ) : (
            <Segmented
              value={type ?? ''}
              onChange={setType}
              ariaLabel="服务类型"
              options={types.map((t) => ({ value: t.code, label: t.label }))}
            />
          )}
        </Field>

        <Field label="哪一年做的" required hint="只要年份就够，不用记月份">
          <div className="flex items-center gap-3">
            <Input
              inputMode="numeric"
              maxLength={4}
              className="tnum w-28"
              value={year}
              onChange={(e) => setYear(e.target.value.replace(/\D/g, ''))}
              placeholder={String(thisYear - 5)}
            />
            <div className="flex flex-wrap gap-1.5">
              {[5, 6, 7, 8].map((n) => (
                <Button key={n} size="sm" onClick={() => setYear(String(thisYear - n))}>
                  {n} 年前
                </Button>
              ))}
            </div>
          </div>
          {year.length === 4 && !yearOk && (
            <p className="mt-2 text-[15px] text-bad">年份要在 1970 到 {thisYear} 之间</p>
          )}
        </Field>

        <Field label="顾客用什么语言" hint="决定以后提醒发出去是什么语言">
          <Segmented
            value={lang}
            onChange={setLang}
            ariaLabel="语言"
            options={[
              { value: 'ms', label: 'Melayu' },
              { value: 'en', label: 'English' },
              { value: 'zh', label: '中文' },
            ]}
          />
        </Field>

        {done.length > 0 && (
          <div className="border-t border-line pt-4">
            <p className="mb-2 flex items-center gap-2 text-[16px] font-semibold">
              <History size={17} /> 这次录了 {done.length} 位
            </p>
            <ul className="flex flex-col gap-1.5">
              {done.map((d, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2 text-[16px]">
                  <Check size={16} className="text-ok" />
                  <span className="font-semibold">{d.customerName}</span>
                  <span className="text-ink-3">
                    {d.typeLabel} · {d.caseNo}
                  </span>
                  {!d.createdCustomer && (
                    <span className="text-[14px] text-ink-3">（挂到了已有档案）</span>
                  )}
                  {d.dueForRenewal && (
                    <span className="rounded-full bg-warn-bg px-2 py-0.5 text-[14px] text-warn">
                      已经该换新了
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[15px] text-ink-3">
              标了「该换新」的，明天上午会自动出现在「待通知」清单里。
              一天最多放 20 位进去，不会一次性淹掉你。
            </p>
            <Button
              className="mt-2"
              size="sm"
              loading={sweep.isPending}
              onClick={() => sweep.mutate()}
            >
              现在就检查一次
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  )
}
